/**
 * PayACHInvoicesModal
 * ────────────────────
 * Vendor ACH Payment modal — supports both CCD+ and CTX NACHA formats.
 *
 * Columns: Checkbox · Vendor/Supplier · Account No · No Of Invoices
 *          · Total Amount Approved · Transaction Ref · ACH Status · NACHA File
 *
 * Transaction references (e.g. PF49-ABS-0001) and NACHA file download
 * links populate AFTER a successful Pay submission.
 */
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Skeleton } from '../ui/skeleton';
import { Label } from '../ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  AlertTriangle, Download, Loader2, Banknote, CheckCircle2,
  FileText, ChevronDown, ChevronRight,
} from 'lucide-react';
import api from '../../lib/api';
import {
  fetchVendorAchPreview, draftCtxNacha, payVendorAch, retransmitVendorAchBatch,
  downloadVendorBatchFile,
} from '../../api/vendorAch';

const fmtC = (v) =>
  v == null
    ? '\u2014'
    : '$' + Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const BADGE_CFG = {
  ach: {
    label: 'ACH',
    cls: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30',
    icon: CheckCircle2,
  },
  no_ach: {
    label: 'No ACH',
    cls: 'bg-slate-200 text-slate-700 border-slate-300',
    icon: null,
  },
  invalid_ach: {
    label: 'Invalid ACH',
    cls: 'bg-red-500/15 text-red-700 border-red-500/30',
    icon: AlertTriangle,
  },
};

export default function PayACHInvoicesModal({
  open, onOpenChange, paymentFileId, paymentFileLabel, accountId, onPaid,
}) {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [drafting, setDrafting] = useState(false);
  const [paying, setPaying] = useState(false);

  // NACHA format selector (default CTX per spec).
  const [achFormat, setAchFormat] = useState('CTX');
  const [bankCode] = useState('ABS'); // future: configurable

  // After Pay: { batchId, filename, vendorRefs }
  const [payResult, setPayResult] = useState(null);
  const [excludedReason, setExcludedReason] = useState('all');
  const [excludedCollapsed, setExcludedCollapsed] = useState(false);

  // Load preview on open
  useEffect(() => {
    if (!open || !paymentFileId) return;
    let cancelled = false;
    setLoading(true);
    setSelected(new Set());
    setPayResult(null);
    fetchVendorAchPreview(paymentFileId, accountId)
      .then((data) => {
        if (cancelled) return;
        setPreview(data);
        const initial = new Set(
          (data.rows || [])
            .filter((r) => r.ach_badge === 'ach')
            .map((r) => r.supplier_id),
        );
        setSelected(initial);
      })
      .catch((err) => {
        toast.error(err.response?.data?.detail || 'Failed to load vendors');
      })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [open, paymentFileId, accountId]);

  // Map supplier_id → transaction_ref for rows after Pay
  const trefBySupplier = useMemo(() => {
    const m = {};
    (payResult?.vendor_refs || []).forEach((v) => { m[v.supplier_id] = v.transaction_ref; });
    return m;
  }, [payResult]);

  const visibleRows = useMemo(() => preview?.rows || [], [preview]);

  // Excluded-bills reason grouping + filter (item: filter excluded list by reason).
  const excludedBills = useMemo(
    () => (Array.isArray(preview?.excluded_bills) ? preview.excluded_bills : []),
    [preview],
  );
  const excludedReasonGroups = useMemo(() => {
    const map = new Map();
    for (const b of excludedBills) {
      const key = b.reason || 'Other';
      map.set(key, (map.get(key) || 0) + 1);
    }
    return Array.from(map.entries()).map(([reason, count]) => ({ reason, count }))
      .sort((a, b) => b.count - a.count);
  }, [excludedBills]);
  const filteredExcluded = useMemo(
    () => (excludedReason === 'all' ? excludedBills : excludedBills.filter((b) => (b.reason || 'Other') === excludedReason)),
    [excludedBills, excludedReason],
  );

  const stats = useMemo(() => {
    let totalSelected = 0;
    let countSelected = 0;
    let noAchSelected = 0;
    let invalidSelected = 0;
    for (const r of visibleRows) {
      if (!selected.has(r.supplier_id)) continue;
      countSelected += 1;
      totalSelected += Number(r.approved_amount) || 0;
      if (r.ach_badge === 'no_ach') noAchSelected += 1;
      if (r.ach_badge === 'invalid_ach') invalidSelected += 1;
    }
    return {
      countSelected,
      totalSelected: Math.round(totalSelected * 100) / 100,
      noAchSelected,
      invalidSelected,
      skipped: noAchSelected + invalidSelected,
    };
  }, [visibleRows, selected]);

  const toggleRow = (sid) => {
    if (payResult) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(sid)) next.delete(sid); else next.add(sid);
      return next;
    });
  };

  const toggleAll = () => {
    if (payResult) return;
    const eligible = visibleRows.filter((r) => r.ach_badge === 'ach').map((r) => r.supplier_id);
    if (eligible.every((s) => selected.has(s))) {
      setSelected((prev) => {
        const next = new Set(prev);
        eligible.forEach((s) => next.delete(s));
        return next;
      });
    } else {
      setSelected(new Set(eligible));
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const getSelectedSupplierIds = () =>
    visibleRows
      .filter((r) => selected.has(r.supplier_id) && r.ach_badge === 'ach')
      .map((r) => r.supplier_id);

  const handleDraft = async () => {
    const ids = getSelectedSupplierIds();
    if (!ids.length) {
      toast.error('Select at least one vendor with ACH setup');
      return;
    }
    setDrafting(true);
    try {
      const res = await draftCtxNacha({
        paymentFileId, supplierIds: ids, accountId,
        ach_format: achFormat, bank_code: bankCode,
      });
      const blob = new Blob([res.data], { type: 'text/plain' });
      const fname = `DRAFT_${achFormat}_${(paymentFileLabel || paymentFileId.slice(0, 8)).replace(/[^A-Za-z0-9]/g, '')}.ach`;
      downloadBlob(blob, fname);
      toast.success(`Draft ${achFormat} NACHA downloaded \u2014 no payment submitted`);
    } catch (err) {
      const detail = err.response?.data;
      let msg = `Failed to draft ${achFormat} NACHA`;
      if (detail) {
        try {
          if (detail instanceof Blob) {
            const text = await detail.text();
            try { msg = JSON.parse(text).detail || msg; } catch { msg = text || msg; }
          } else {
            msg = detail.detail || msg;
          }
        } catch { /* noop */ }
      }
      toast.error(msg);
    } finally {
      setDrafting(false);
    }
  };

  const handlePay = async () => {
    const ids = getSelectedSupplierIds();
    if (!ids.length) {
      toast.error('Select at least one vendor with ACH setup');
      return;
    }
    if (!window.confirm(
      `Confirm ${achFormat} payment to ${ids.length} vendor${ids.length === 1 ? '' : 's'} totaling ${fmtC(stats.totalSelected)}? This will mark invoices as paid.`,
    )) return;
    setPaying(true);
    try {
      const res = await payVendorAch({
        paymentFileId, supplierIds: ids, accountId,
        ach_format: achFormat, bank_code: bankCode,
      });
      setPayResult(res);

      // Map sftp_status → toast copy / type. The backend always returns a
      // 200 even on SFTP failure (so the modal can surface a retransmit
      // affordance without re-running pay).
      const sftpStatus = res.sftp_status || (res.sftp_uploaded ? 'uploaded' : 'skipped');
      const summary = `Generated ${achFormat} NACHA for ${res.vendor_count} vendor${res.vendor_count === 1 ? '' : 's'} — ${fmtC(res.total_amount)}`;
      if (sftpStatus === 'uploaded') {
        toast.success(`${summary}. Uploaded to bank SFTP — bills marked paid.`, { duration: 8000 });
      } else if (sftpStatus === 'failed') {
        toast.error(
          `${summary}. SFTP upload FAILED — bills NOT marked paid. Retry from Batch History. (${res.sftp_error || 'see logs'})`,
          { duration: 12000 },
        );
      } else {
        // 'skipped' — no SFTP configured. Bills are NOT marked paid.
        toast.error(
          `${summary}. No bank SFTP configured — bills NOT marked paid. Configure Settings → SFTP, then click Retry SFTP.`,
          { duration: 12000 },
        );
      }
      onPaid?.(res);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payment failed');
    } finally {
      setPaying(false);
    }
  };

  const handleDownloadBatch = async () => {
    if (!payResult?.batch_id) return;
    try {
      const res = await api.get(
        `/vendor-ach/batches/${payResult.batch_id}/download`,
        { responseType: 'blob', params: accountId ? { account_id: accountId } : {} },
      );
      const blob = new Blob([res.data], { type: 'text/plain' });
      downloadBlob(blob, payResult.filename);
    } catch (err) {
      toast.error('Failed to download NACHA file');
    }
  };

  const [retransmitting, setRetransmitting] = useState(false);
  // Per-vendor NACHA file download (NACHA File column). Tracks the currently
  // downloading supplier so we can show a spinner instead of the download icon.
  const [downloadingSupplier, setDownloadingSupplier] = useState(null);

  const handleDownloadVendorFile = async (row) => {
    setDownloadingSupplier(row.supplier_id);
    try {
      const committedTref = trefBySupplier[row.supplier_id];
      const vendorSlug = (row.vendor || 'Vendor').replace(/[^A-Za-z0-9]/g, '').slice(0, 30) || 'Vendor';
      const pfSlug = (paymentFileLabel || paymentFileId.slice(0, 8)).replace(/[^A-Za-z0-9]/g, '');
      if (committedTref && payResult?.batch_id) {
        // After Pay — pull the committed per-vendor file from the batch.
        const res = await downloadVendorBatchFile(
          payResult.batch_id, row.supplier_id, accountId,
        );
        const fname = `${achFormat}_${pfSlug}_${vendorSlug}.ach`;
        downloadBlob(new Blob([res.data], { type: 'text/plain' }), fname);
        toast.success(`Downloaded ${achFormat} NACHA for ${row.vendor}`);
        return;
      }
      // Before Pay — generate a single-vendor draft on the fly.
      const res = await draftCtxNacha({
        paymentFileId, supplierIds: [row.supplier_id], accountId,
        ach_format: achFormat, bank_code: bankCode,
      });
      const fname = `DRAFT_${achFormat}_${pfSlug}_${vendorSlug}.ach`;
      downloadBlob(new Blob([res.data], { type: 'text/plain' }), fname);
      toast.success(`Draft ${achFormat} NACHA downloaded for ${row.vendor}`);
    } catch (err) {
      const detail = err.response?.data;
      let msg = `Failed to download ${achFormat} NACHA`;
      if (detail) {
        try {
          if (detail instanceof Blob) {
            const text = await detail.text();
            try { msg = JSON.parse(text).detail || msg; } catch { msg = text || msg; }
          } else {
            msg = detail.detail || msg;
          }
        } catch { /* noop */ }
      }
      toast.error(msg);
    } finally {
      setDownloadingSupplier(null);
    }
  };

  const handleRetransmit = async () => {
    if (!payResult?.batch_id) return;
    setRetransmitting(true);
    try {
      const res = await retransmitVendorAchBatch(payResult.batch_id, accountId);
      setPayResult((prev) => ({
        ...prev,
        sftp_status: res.sftp_status,
        sftp_uploaded: res.sftp_uploaded,
        sftp_remote_filename: res.sftp_remote_filename,
        sftp_remote_path: res.sftp_remote_path,
        sftp_error: res.sftp_status === 'failed' ? res.message : null,
        bills_marked_paid: res.sftp_status === 'uploaded' ? true : prev.bills_marked_paid,
      }));
      if (res.sftp_uploaded) {
        toast.success(`Uploaded ${res.sftp_remote_filename} to bank SFTP`, { duration: 8000 });
        onPaid?.(res);
      } else {
        toast.error(res.message || 'Retransmit failed', { duration: 10000 });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Retransmit failed');
    } finally {
      setRetransmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-6xl rounded-none max-h-[90vh] flex flex-col p-0 gap-0"
        data-testid="pay-ach-invoices-modal"
      >
        <DialogHeader className="px-6 pt-6 pb-3 flex-shrink-0 border-b">
          <DialogTitle className="flex items-center gap-2 text-xl font-bold tracking-tight">
            <Banknote className="h-5 w-5 text-emerald-600" />
            Pay Invoices through ACH
            {paymentFileLabel && (
              <span className="text-sm font-normal text-muted-foreground">
                — {paymentFileLabel}
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Vendor ACH Payment · ACH Credit · {achFormat} NACHA · uses Payment File invoice/vendor data
          </DialogDescription>
        </DialogHeader>

        {/* Single scrollable body — keeps the footer pinned and avoids the
            nested/competing scrollbars that made the modal hard to use. */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4">

        {/* ACH Format selector */}
        <div className="flex items-center gap-6 border-b pb-3">
          <div className="flex items-center gap-3">
            <Label htmlFor="ach-format" className="text-xs font-semibold text-foreground">
              ACH Format <span className="text-red-500">*</span>
            </Label>
            <Select
              value={achFormat}
              onValueChange={setAchFormat}
              disabled={!!payResult || paying}
            >
              <SelectTrigger
                id="ach-format"
                className="h-8 w-28 text-xs"
                data-testid="ach-format-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="CCD" data-testid="ach-format-option-ccd">CCD+</SelectItem>
                <SelectItem value="CTX" data-testid="ach-format-option-ctx">CTX</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-[11px] text-muted-foreground">
              {achFormat === 'CCD'
                ? 'Single summary addenda per vendor'
                : 'One addenda per invoice (recommended for vendor remittance)'}
            </span>
          </div>
          <div className="ml-auto flex items-center gap-6 text-sm">
            <div data-testid="pay-ach-selected-count">
              <span className="font-semibold">{stats.countSelected}</span>
              <span className="ml-1 text-muted-foreground">{stats.countSelected === 1 ? 'Vendor' : 'Vendors'} Selected</span>
            </div>
            <div data-testid="pay-ach-total">
              <span className="text-muted-foreground">Total </span>
              <span className="font-mono font-semibold">{fmtC(stats.totalSelected)}</span>
            </div>
          </div>
        </div>

        {/* Warning banner */}
        {stats.skipped > 0 && !payResult && (
          <div
            className="flex items-start gap-2 border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800"
            data-testid="pay-ach-warning-banner"
          >
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>
              <strong>{stats.skipped}</strong> vendor{stats.skipped === 1 ? '' : 's'} without ACH setup — {achFormat} NACHA generation will skip {stats.skipped === 1 ? 'this vendor' : 'these vendors'}.
            </span>
          </div>
        )}

        {/* Post-Pay summary banner — color-coded by SFTP outcome. */}
        {payResult && (() => {
          const sftpStatus = payResult.sftp_status
            || (payResult.sftp_uploaded ? 'uploaded' : 'skipped');
          const banner = sftpStatus === 'failed'
            ? { cls: 'border-rose-300 bg-rose-50 text-rose-800', icon: AlertTriangle, label: 'SFTP upload failed', iconCls: 'text-rose-700' }
            : sftpStatus === 'uploaded'
              ? { cls: 'border-emerald-300 bg-emerald-50 text-emerald-800', icon: CheckCircle2, label: 'Uploaded to bank SFTP', iconCls: 'text-emerald-700' }
              : { cls: 'border-amber-300 bg-amber-50 text-amber-800', icon: AlertTriangle, label: 'SFTP not configured — bills NOT marked paid', iconCls: 'text-amber-700' };
          const BIcon = banner.icon;
          const billsPaid = payResult.bills_marked_paid !== false
            && sftpStatus === 'uploaded';
          const canRetry = sftpStatus === 'failed' || sftpStatus === 'skipped';
          return (
            <div
              className={`flex flex-wrap items-center gap-2 border p-3 text-sm ${banner.cls}`}
              data-testid="pay-ach-success-banner"
            >
              <BIcon className={`h-4 w-4 flex-shrink-0 ${banner.iconCls}`} />
              <span>
                Generated {payResult.ach_format} NACHA for {payResult.vendor_count} vendor{payResult.vendor_count === 1 ? '' : 's'} —
                {' '}<strong>{fmtC(payResult.total_amount)}</strong>.
                {' '}<strong>{banner.label}</strong>.
                {' '}Bills: <strong>{billsPaid ? 'marked paid' : 'NOT marked paid'}</strong>.
                {payResult.sftp_error && (
                  <span className="block mt-1 text-xs opacity-90 font-mono">{payResult.sftp_error}</span>
                )}
              </span>
              <div className="ml-auto flex items-center gap-2">
                {canRetry && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2"
                    onClick={handleRetransmit}
                    disabled={retransmitting}
                    data-testid="pay-ach-retransmit-btn"
                  >
                    {retransmitting ? (
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    ) : (
                      <Banknote className="h-3.5 w-3.5 mr-1.5" />
                    )}
                    Retry SFTP
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2"
                  onClick={handleDownloadBatch}
                  data-testid="pay-ach-download-batch-btn"
                >
                  <Download className="h-3.5 w-3.5 mr-1.5" />
                  {payResult.filename}
                </Button>
              </div>
            </div>
          );
        })()}

        {/* Table */}
        <div className="border border-border">
          <Table>
            <TableHeader className="sticky top-0 bg-card z-10">
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    data-testid="pay-ach-select-all"
                    checked={
                      visibleRows.length > 0 &&
                      visibleRows.filter((r) => r.ach_badge === 'ach').every((r) => selected.has(r.supplier_id))
                    }
                    onCheckedChange={toggleAll}
                    disabled={!!payResult}
                  />
                </TableHead>
                <TableHead>Vendor / Supplier</TableHead>
                <TableHead>Account No</TableHead>
                <TableHead className="text-right">No Of Invoices</TableHead>
                <TableHead className="text-right">Total Amount Approved</TableHead>
                <TableHead>Transaction Ref</TableHead>
                <TableHead>ACH Status</TableHead>
                <TableHead>NACHA File</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <>
                  {[...Array(4)].map((_, i) => (
                    <TableRow key={`sk-${i}`}>
                      <TableCell colSpan={8}><Skeleton className="h-6 w-full" /></TableCell>
                    </TableRow>
                  ))}
                </>
              )}
              {!loading && visibleRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-8">
                    No unpaid vendors on this Payment File.
                  </TableCell>
                </TableRow>
              )}
              {!loading && visibleRows.map((r) => {
                const cfg = BADGE_CFG[r.ach_badge] || BADGE_CFG.no_ach;
                const Icon = cfg.icon;
                const isEligible = r.ach_badge === 'ach';
                const tref = trefBySupplier[r.supplier_id];
                const isPaidRow = !!tref;
                return (
                  <TableRow
                    key={r.supplier_id}
                    data-testid={`pay-ach-row-${r.supplier_id}`}
                    className={!isEligible ? 'opacity-60' : ''}
                  >
                    <TableCell>
                      <Checkbox
                        data-testid={`pay-ach-row-checkbox-${r.supplier_id}`}
                        checked={selected.has(r.supplier_id)}
                        disabled={!isEligible || !!payResult}
                        onCheckedChange={() => toggleRow(r.supplier_id)}
                      />
                    </TableCell>
                    <TableCell className="font-medium text-sm">{r.vendor}</TableCell>
                    <TableCell className="font-mono text-xs">{r.vendor_account_no || '—'}</TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {r.invoice_count || (r.bill_ids?.length ?? 0)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {fmtC(r.approved_amount)}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {tref ? (
                        <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-emerald-700">
                          {tref}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-xs ${cfg.cls}`}>
                        {Icon && <Icon className="h-3 w-3 mr-1" />}
                        {isPaidRow ? 'Paid' : cfg.label}
                        {r.ach_last4 && r.ach_badge === 'ach' && (
                          <span className="ml-1 opacity-70">··{r.ach_last4}</span>
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      {isEligible ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2"
                          onClick={() => handleDownloadVendorFile(r)}
                          disabled={downloadingSupplier === r.supplier_id}
                          title={isPaidRow
                            ? `Download committed ${achFormat} NACHA for ${r.vendor}`
                            : `Download draft ${achFormat} NACHA for ${r.vendor}`}
                          data-testid={`pay-ach-row-download-${r.supplier_id}`}
                        >
                          {downloadingSupplier === r.supplier_id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Download className="h-3.5 w-3.5" />
                          )}
                          <span className="ml-1.5 text-xs">
                            {isPaidRow ? 'NACHA' : 'Draft'}
                          </span>
                        </Button>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        {/* Excluded bills — surfaced so admins can see at a glance why a
            bill is missing from the batch ($0 approved, customer-paid,
            already paid, etc.). Server-side filter lives in
            services/vendor_ach/service.py::build_pf_vendor_preview. */}
        {excludedBills.length > 0 && (
          <div className="mt-2 border border-amber-200 bg-amber-50/40 rounded-none" data-testid="pay-ach-excluded-panel">
            <button
              type="button"
              onClick={() => setExcludedCollapsed((c) => !c)}
              className="w-full px-3 py-2 flex flex-wrap items-center gap-2 border-b border-amber-200 text-left hover:bg-amber-50/70 transition-colors"
              data-testid="pay-ach-excluded-toggle"
            >
              {excludedCollapsed
                ? <ChevronRight className="h-4 w-4 text-amber-700 flex-shrink-0" />
                : <ChevronDown className="h-4 w-4 text-amber-700 flex-shrink-0" />}
              <span className="inline-flex items-center gap-1.5 border border-amber-700/70 bg-white px-2 py-0.5 text-sm font-semibold">
                <AlertTriangle className="h-3.5 w-3.5 text-amber-700" />
                {excludedBills.length} bill{excludedBills.length === 1 ? '' : 's'} excluded from this batch
              </span>
              <span className="text-xs text-muted-foreground ml-1">
                (will not be transmitted via SFTP)
              </span>
            </button>

            {!excludedCollapsed && (
              <>
                {/* Reason filter pills with per-reason counts */}
                <div className="px-3 py-2 flex flex-wrap items-center gap-2 border-b border-amber-200/70">
                  <button
                    type="button"
                    onClick={() => setExcludedReason('all')}
                    data-testid="pay-ach-excluded-reason-all"
                    className={`px-2.5 py-1 text-xs rounded-none border transition-colors ${
                      excludedReason === 'all'
                        ? 'bg-slate-800 text-white border-slate-800'
                        : 'bg-white text-slate-700 border-slate-300 hover:border-slate-400'
                    }`}
                  >
                    All · {excludedBills.length}
                  </button>
                  {excludedReasonGroups.map((g) => {
                    const isZero = /\$?0\.00|zero/i.test(g.reason);
                    const active = excludedReason === g.reason;
                    return (
                      <button
                        key={g.reason}
                        type="button"
                        onClick={() => setExcludedReason(g.reason)}
                        data-testid={`pay-ach-excluded-reason-pill-${g.reason.replace(/\s+/g, '-').toLowerCase()}`}
                        className={`px-2.5 py-1 text-xs rounded-none border transition-colors ${
                          active
                            ? 'ring-1 ring-offset-0 ring-slate-700 '
                            : ''
                        }${
                          isZero
                            ? 'bg-amber-100 text-amber-800 border-amber-300 hover:border-amber-400'
                            : 'bg-violet-50 text-violet-700 border-violet-200 hover:border-violet-300'
                        }`}
                      >
                        {g.reason} · {g.count}
                      </button>
                    );
                  })}
                </div>

                <div className="max-h-[300px] overflow-y-auto">
                  <Table>
                    <TableHeader className="sticky top-0 bg-amber-50 z-10">
                      <TableRow className="bg-amber-50/80">
                        <TableHead className="h-8">Vendor</TableHead>
                        <TableHead className="h-8">Account No</TableHead>
                        <TableHead className="h-8">Invoice #</TableHead>
                        <TableHead className="h-8 text-right">Approved</TableHead>
                        <TableHead className="h-8">Excluded — reason</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredExcluded.map((b) => (
                        <TableRow key={b.bill_id} data-testid={`pay-ach-excluded-row-${b.bill_id}`}>
                          <TableCell className="text-sm">{b.vendor || '—'}</TableCell>
                          <TableCell className="font-mono text-xs">{b.account_number || '—'}</TableCell>
                          <TableCell className="font-mono text-xs">{b.invoice_number || '—'}</TableCell>
                          <TableCell className="text-right font-mono text-sm">
                            {fmtC(b.amount)}
                          </TableCell>
                          <TableCell className="text-xs">
                            <Badge
                              variant="outline"
                              className={
                                b.reason_code === 'zero_amount'
                                  ? 'rounded-none bg-amber-100 text-amber-800 border-amber-300'
                                  : b.reason_code === 'client_paid'
                                  ? 'rounded-none bg-sky-100 text-sky-800 border-sky-300'
                                  : 'rounded-none bg-violet-50 text-violet-700 border-violet-200'
                              }
                            >
                              {b.reason}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </div>
        )}
        </div>

        <DialogFooter className="flex items-center justify-between sm:justify-between px-6 py-4 border-t flex-shrink-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="pay-ach-cancel-btn"
          >
            {payResult ? 'Close' : 'Cancel'}
          </Button>
          <div className="flex items-center gap-3">
            {!payResult && (
              <>
                <Button
                  variant="outline"
                  onClick={handleDraft}
                  disabled={drafting || paying || stats.countSelected === 0}
                  data-testid="pay-ach-draft-nacha-btn"
                >
                  {drafting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  Draft {achFormat} NACHA
                </Button>
                <Button
                  onClick={handlePay}
                  disabled={drafting || paying || stats.countSelected === 0}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="pay-ach-pay-btn"
                >
                  {paying ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Pay
                </Button>
              </>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
